<template>
    <div class="flex border-t border-b -mb-px border-gray-200 bg-white draggable">
        <div class="flex flex-1" :class="readOnly ? 'truncate' : ''">
            <div
                v-if="readOnly"
                class="flex-1 text-sm -my-px px-2 py-1.5 border border-gray-200 shadow-none relative truncate"
                v-text="item.name"
                :title="item.name"
            ></div>
            <materials-search-dropdown class="flex-1" v-else #default="{ open, opened, query, setQuery }" :uom="form.unit_of_measure" @selected="updateMaterial" :estimate="estimate" :current="item.name">
                <input
                    class="text-sm -my-px px-2 py-1.5 border border-gray-200 shadow-none relative hover:z-10 focus:z-20"
                    type="text"
                    ref="description"
                    :value="opened ? query : item.name"
                    :title="item.name"
                    @input="setQuery($event.target.value)"
                    @focus="open"
                >
            </materials-search-dropdown>
        </div>
        <div class="flex w-40 -ml-px" :class="readOnly ? 'truncate' : ''">
            <div
                v-if="readOnly"
                class="flex-1 text-sm -my-px px-2 py-1.5 border border-gray-200 shadow-none relative truncate"
                :class="hasSupplier ? '' : 'text-gray-500 italic'"
                v-text="supplierName"
            ></div>
            <materials-suppliers-search-dropdown v-else #default="{ open, opened, query, setQuery }" :material-spec="item" @selected="updateSupplier" :estimate="estimate">
                <input
                    class="text-sm -my-px px-2 py-1.5 border border-gray-200 shadow-none relative hover:z-10 focus:z-20"
                    :class="hasSupplier ? '' : 'text-gray-500 italic'"
                    type="text"
                    ref="description"
                    :value="opened ? query : supplierName"
                    :title="supplierName"
                    @input="setQuery($event.target.value)"
                    @focus="open"
                >
            </materials-suppliers-search-dropdown>
        </div>
        <global-specs-form-price-cells
            class="ml-px"
            :item="item"
            :uom="form.unit_of_measure"
            :read-only="readOnly"
            :price.sync="item.price_per_item"
            :coverage.sync="item.coverage"
            :rate.sync="item.price_per_uom"
            @update:price="updateMaterialPrice"
            @update:coverage="$emit('updated')"
            @update:rate="$emit('updated')"
        >
            <template #price="{ price, updatePrice }">
                <materials-price-update-dropdown #default="{ inputValue, inputListener }" :material-spec="item" @updated="updatePrice" class="flex w-full" trigger-class="flex w-full">
                    <estimates-old-page-cell
                        currency
                        class="text-sm text-right -ml-px -my-px px-2 py-1.5 border border-gray-200"
                        placeholder="Price"
                        :value="inputValue"
                        @input="inputListener"
                    ></estimates-old-page-cell>
                </materials-price-update-dropdown>
            </template>
            <template #rate="{ rate, updatePrice }">
                <materials-price-update-dropdown #default="{ inputListener }" :material-spec="item" @updated="updatePrice" align-right class="flex w-full" trigger-class="flex w-full">
                    <estimates-old-page-cell
                        currency
                        class="text-sm text-right -ml-px -my-px px-2 py-1.5 border border-gray-200"
                        placeholder="Rate"
                        :value="rate"
                        @input="confirmRate($event, inputListener)"
                    ></estimates-old-page-cell>
                </materials-price-update-dropdown>
            </template>
        </global-specs-form-price-cells>
        <div class="w-32 -ml-px border-r border-gray-200 bg-gray-50 px-2 py-1.5 text-sm text-right" v-if="showTotal">
            {{ total | toCurrency }}
        </div>
        <div class="w-8 flex-shrink-0 px-1 border-r border-gray-200 flex items-center justify-center">
            <ui-dropdown placement="bottom-end" menu-class="p-3 w-48" v-if="! readOnly">
                <template #default="{ toggle }">
                    <button @click.prevent="toggle" class="text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative p-1 rounded">
                        <i class="fa fa-ellipsis-h"></i>
                    </button>
                </template>

                <template #menu>
                    <ui-dropdown-item icon="fas fa-trash" danger @click.stop="deleteMaterial">Delete</ui-dropdown-item>
                </template>
            </ui-dropdown>
        </div>
    </div>
</template>

<script>
import { asRoundedNumber } from '@utils'

export default {
    props: {
        form: Object,
        estimate: Object,
        item: Object,
        quantity: Number,
        readOnly: Boolean,
    },
    computed: {
        showTotal () {
            return this.quantity !== undefined
        },
        total () {
            return this.item.price_per_uom * (this.quantity ?? 0)
        },
        hasSupplier () {
            return !! this.item.company_id
        },
        supplierName () {
            if (this.hasSupplier) return this.item.company.name
            return this.estimate ? 'No Supplier' : 'Default Supplier'
        },
    },
    methods: {
        updateMaterial (material) {
            this.item.price_per_item = 0
            this.item.name = material.name
            this.item.material = {
                id: material.id,
                name: material.name,
            }

            const newSupplier = material.current_company ?? material.default_company ?? null
            this.updateSupplier(newSupplier, true)
        },
        deleteMaterial () {
            this.form.materials = this.form.materials
                .filter(materialLine => materialLine !== this.item)
            this.$emit('updated')
        },
        findAttachedCompany (companyId) {
            if (! companyId) return null
            return this.item.attached_companies
                .find(company => company.company_id == companyId)
        },
        updateSupplier (supplier, priceOnly = false) {
            supplier ??= {
                company_id: null,
                company_name: null,
                cost: this.item.default_company?.default_cost ?? 0,
            }

            this.item.price_per_item = supplier.cost
            this.item.price_per_uom = this.item.coverage
                ? asRoundedNumber(parseFloat(this.item.price_per_item / this.item.coverage),3)
                : 0

            if (priceOnly) {
                return this.$emit('updated')
            }

            this.item.company_id = supplier.company_id
            this.item.company = {
                id: supplier.company_id,
                name: supplier.company_name,
            }

            if (supplier.company_id && ! this.findAttachedCompany(supplier.company_id)) {
                this.item.attached_companies.push({
                    ...supplier,
                    is_attached: true,
                })
            }

            this.$emit('updated')
        },
        updateMaterialPrice (newPrice) {
            const attachedSupplier = this.findAttachedCompany(this.item.company_id)

            if (attachedSupplier) {
                attachedSupplier.cost = newPrice
                attachedSupplier.is_from_mpa = attachedSupplier.default_cost != newPrice
            }

            this.$emit('updated')
        },
        confirmRate (newRate, inputListener) {
            newRate = asRoundedNumber(newRate,3)
            const newPrice = asRoundedNumber(newRate * (this.coverage ?? 1.00),3)
            inputListener(newPrice)
        }
    },
}
</script>
